import type { ControllerProps } from "react-hook-form";
import { t } from "ttag";

import type { Form } from "@/features/forms/models";
import {
  ACCEPT_IMAGE_FILE,
  FIELD_TYPE,
  MAX_UPLOAD_SIZE_PLACEHOLDER,
} from "@/shared/models/legacy-constants";
import {
  CheckboxGroup,
  Dropzone,
  Input,
  MarkdownContent,
  RadioGroup,
  Select,
  TextArea,
} from "@/shared/ui";

type FieldProps = Parameters<ControllerProps["render"]>[number] & {
  healthieField: Form["fields"][number];
  id?: string;
};

export const Field = ({ field, healthieField, id, fieldState }: FieldProps) => {
  const options = healthieField.options.map((option) => ({
    value: option,
    label: option,
  }));

  const placeholder = !healthieField.required
    ? t`Leave blank if you don't have this information`
    : undefined;
  const props = { ...field, id, "aria-invalid": fieldState.invalid };

  switch (healthieField.type) {
    case FIELD_TYPE.FILE:
      return (
        <Dropzone
          {...props}
          onDrop={(acceptedFiles) => props.onChange(acceptedFiles[0])}
          accept={ACCEPT_IMAGE_FILE}
          placeholder={MAX_UPLOAD_SIZE_PLACEHOLDER}
        />
      );

    case FIELD_TYPE.HORIZONTAL_RADIO:
      return (
        <RadioGroup.Root
          className="flex items-center gap-6"
          onValueChange={props.onChange}
          {...props}
        >
          {options.map(({ value, label }) => (
            <RadioGroup.Item key={value} value={value}>
              <RadioGroup.Label>
                <MarkdownContent>{label}</MarkdownContent>
              </RadioGroup.Label>
            </RadioGroup.Item>
          ))}
        </RadioGroup.Root>
      );

    case FIELD_TYPE.RADIO:
      return (
        <RadioGroup.Root onValueChange={props.onChange} {...props}>
          {options.map(({ value, label }) => (
            <RadioGroup.Item key={value} value={value}>
              <RadioGroup.Label>
                <MarkdownContent>{label}</MarkdownContent>
              </RadioGroup.Label>
            </RadioGroup.Item>
          ))}
        </RadioGroup.Root>
      );

    case FIELD_TYPE.CHECKBOX:
      return <CheckboxGroup options={options} {...props} />;

    case FIELD_TYPE.DROPDOWN:
      return <Select options={options} {...props} />;

    case FIELD_TYPE.TEXT:
      return <Input.Root placeholder={placeholder} {...props} />;

    case FIELD_TYPE.NUMBER:
      return <Input.Root type="number" {...props} min={0} />;

    case FIELD_TYPE.TEXTAREA:
      return <TextArea placeholder={placeholder} {...props} />;
  }
};
